import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { Heart, MessageCircle, Calendar, Tag as TagIcon } from "lucide-react";
import PostDetailModal from "./PostDetailModal";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "update", label: "Updates" },
  { id: "announcement", label: "Announcements" },
  { id: "news", label: "News" },
  { id: "behind-the-scenes", label: "Behind the Scenes" },
  { id: "exclusive", label: "Exclusive" },
];

export default function PostsFeed() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPost, setSelectedPost] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error,setError]=useState("");
  const [loadingMore,setLoadingMore]=useState(false);
  const requestVersion=useRef(0);

  const loadPosts = useCallback(async (loadMore = false, currentOffset = 0) => {
    const version=++requestVersion.current;
    setError("");
    if(loadMore)setLoadingMore(true);
    try {
      const res = await api.get(`/api/posts-updates/public?category=${selectedCategory}&limit=12&offset=${currentOffset}`);
      if(version!==requestVersion.current)return;
      const rows=Array.isArray(res.data?.posts)?res.data.posts:[];
      
      if (loadMore) {
        setPosts((currentPosts) => [...currentPosts, ...rows]);
      } else {
        setPosts(rows);
      }
      
      setHasMore(Boolean(res.data?.has_more));
      setOffset(currentOffset + rows.length);
    } catch (e) {
      if(version===requestVersion.current)setError("Unable to load posts. Please try again.");
    } finally {
      if(version===requestVersion.current){setLoading(false);setLoadingMore(false);}
    }
  }, [selectedCategory]);

  // Declare the callback before using it in the dependency array: reading a
  // const in its temporal dead zone crashes the entire member area on mount.
  useEffect(() => {
    loadPosts();
    return()=>{requestVersion.current+=1;};
  }, [loadPosts]);

  const handleCategoryChange = (category) => {
    if(category===selectedCategory)return;
    setSelectedCategory(category);
    setOffset(0);
    setLoading(true);
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
    // Reload posts to get updated counts
    loadPosts();
  };

  const getCategoryColor = (category) => {
    const colors = {
      update: "from-blue-500 to-blue-600",
      announcement: "from-[#E11D48] to-[#FF10F0]",
      news: "from-purple-500 to-purple-600",
      "behind-the-scenes": "from-amber-500 to-amber-600",
      exclusive: "from-green-500 to-green-600",
    };
    return colors[category] || "from-gray-500 to-gray-600";
  };

  return (
    <div className="mb-12">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
          Posts & Updates
        </h2>
        <p className="text-sm text-zinc-400">
          Stay connected with the latest news, announcements, and exclusive content
        </p>
      </div>

      {/* Category Tabs */}
      <div className="overflow-x-auto mb-6">
        <div className="flex gap-2 min-w-fit">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? "bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white"
                  : "bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Posts Grid */}
      {error && <div role="alert" className="mb-4 rounded-xl border border-rose-300/30 p-4 text-sm text-rose-200">{error}<button type="button" onClick={()=>loadPosts()} className="ml-3 underline">Retry</button></div>}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white/5 rounded-lg animate-pulse h-80" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-zinc-400">
          <p>No posts yet in this category</p>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((post) => (
              <div
                key={post.id}
                onClick={() => handlePostClick(post)}
                className="bg-white/5 rounded-lg overflow-hidden border border-white/10 hover:border-[#E11D48]/50 transition-all cursor-pointer group"
              >
                {/* Featured Image */}
                <div className="relative h-48 overflow-hidden">
                  {post.featured_image ? (
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${getCategoryColor(post.category)}`} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-bold text-lg mb-2 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-zinc-400 mb-3 line-clamp-2">
                    {post.excerpt}
                  </p>

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-zinc-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Heart size={14} />
                        {post.likes_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle size={14} />
                        {post.comments_count}
                      </span>
                    </div>
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="text-center mt-6">
              <button
                onClick={() => loadPosts(true, offset)}
                disabled={loadingMore}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-lg font-semibold transition-colors"
              >
                {loadingMore?"Loading…":"Load More"}
              </button>
            </div>
          )}
        </>
      )}

      {/* Post Detail Modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}
